import { Fragment, useMemo } from 'react';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';

import { useGetProductCatalogQuery } from '@/api/hooks/queries/use-get-product-catalog-query';
import { useGetProductCategoriesQuery } from '@/api/hooks/queries/use-get-product-categories-query';
import Hr from '@/components/common/Hr.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import {
  PRODUCT_STEPS,
  PRODUCT_STEP_URL_NO_PRODUCTS_SELECTED,
  PRODUCT_STEP_URL_SUMMARY,
  getCategoryStep,
} from '@/lib/products';
import type { TanstackUrl } from '@/lib/stepper.ts';
import {
  type Product,
  type ProductCategory,
  groupByCategory,
  groupById,
} from '@/types/product.type';
import ProductStepper from '@/views/layouts/ProductStepper.tsx';

export default function ProductSummary() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const { selectedProductIds, insuranceBuilder, wizardVariantId } = useSessionStore();

  const catalogQuery = useGetProductCatalogQuery();
  const products = useMemo(
    () => (catalogQuery.data ?? []).filter((p) => selectedProductIds.includes(p.uuid)),
    [catalogQuery.data, selectedProductIds]
  );

  const { data: categories = [] } = useGetProductCategoriesQuery();

  const orderedCategoryIds = useMemo(
    () =>
      PRODUCT_STEPS.map((step) => step.id)
        .map((categoryName) => categories.find((c) => c.label === categoryName))
        .filter((cat): cat is ProductCategory => cat != null)
        .map((cat) => cat.uuid),
    [categories]
  );
  const productsByCategory = useMemo(() => groupByCategory(products), [products]);
  const categoriesById = useMemo(() => groupById(categories), [categories]);

  if (catalogQuery.isSuccess && products.length === 0) {
    return <Navigate to={PRODUCT_STEP_URL_NO_PRODUCTS_SELECTED} replace />;
  }

  const handleOnSubmit = () => {
    // Mark product selection as confirmed
    useSessionStore.getState().updateState({ productSelectionConfirmed: true });

    // Auto-save session (non-blocking)
    saveSession();

    if (
      wizardVariantId !== 'eligibility-revamp' &&
      insuranceBuilder.unprovidedInsurances.length > 0
    ) {
      navigate({ to: '/insurance/reach-out' });
      return;
    }

    navigate({ to: '/appointment/intro' });
  };

  return (
    <section>
      <ProductStepper />
      <Card className="gap-3">
        <CardHeader>
          <CardTitle>Review your selections</CardTitle>
          <CardDescription className="mt-2 border-none pl-0 text-gray-400">
            {products.length} {products.length === 1 ? 'item' : 'items'} chosen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4.5">
          <div className="mb-4.5">
            {orderedCategoryIds.map((categoryId) => (
              <Fragment key={categoryId}>
                {productsByCategory.get(categoryId) != null && (
                  <>
                    <CategorySummary
                      category={categoriesById.get(categoryId)}
                      products={productsByCategory.get(categoryId) ?? []}
                    />
                    <Hr />
                  </>
                )}
              </Fragment>
            ))}
          </div>

          <div className="mx-3 mb-8">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-spruce-500">Estimated Total Cost:</h3>
              <div className="text-lg font-semibold text-spruce-500">$0.00</div>
            </div>
            <p className="text-sm text-main">
              This should be 100% covered by your benefits, we’ll reach out if we discover any
              co-insurance due.
            </p>
          </div>

          <Button className="w-full" onClick={handleOnSubmit}>
            Confirm
          </Button>
        </CardContent>
      </Card>
    </section>
  );
}

function CategorySummary({
  category,
  products,
}: {
  category: ProductCategory | undefined;
  products: Product[];
}) {
  const editUrl = useMemo(() => {
    const step = category ? getCategoryStep(category.label) : undefined;
    if (!step) return PRODUCT_STEP_URL_SUMMARY;
    return `${step.url}?nextStep=${PRODUCT_STEP_URL_SUMMARY}`;
  }, [category]);

  return (
    <div className="m-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-main">
          {category ? category.label : 'Uncategorized'}
        </h4>
        {/* TODO Figure out how to get zod to validate search params!! */}
        <Link to={editUrl as TanstackUrl} className="text-[16px] text-hydrangea-500 underline">
          Edit
        </Link>
      </div>
      <ul className="space-y-2">
        {products.map((product) => (
          <li key={product.uuid}>{product.name}</li>
        ))}
      </ul>
    </div>
  );
}
